<template>
    <v-card class="round-card background-2 mb-4">
        <v-card-title class="pa-3">
            <v-icon color="purple" class="mr-3">mdi-share</v-icon>
            <p>Inoltra</p>
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text>
            <v-form ref="form" v-model="valid">
                <!-- To Recipient -->
                <v-combobox
                    v-model="form.to"
                    :items="emailsTo"
                    label="A"
                    v-validate="'email|required'"
                    :data-vv-name="'A'"
                    :error-messages="errors.collect('A')"
                    outlined
                    dense
                    class="mb-0"
                    :return-object="false"
                >
                    <template v-slot:item="data">
                        <v-list-item-avatar size="32px" class="primary mr-4">
                            <v-icon color="white" small>mdi-email</v-icon>
                        </v-list-item-avatar>
                        <v-list-item-content>
                            <v-list-item-title>{{ data.item.text || data.item }}</v-list-item-title>
                            <v-list-item-subtitle v-if="data.item.subtitle">{{ data.item.subtitle }}</v-list-item-subtitle>
                        </v-list-item-content>
                    </template>
                </v-combobox>

                <!-- CC Recipients -->
                <v-combobox
                    v-model="form.cc"
                    label="CC (opzionale)"
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    v-validate="'email'"
                    :data-vv-name="'CC'"
                    :error-messages="errors.collect('CC')"
                    hint="Premi invio per aggiungere un indirizzo"
                    persistent-hint
                    outlined
                    dense
                    class="mb-0"
                >
                    <template v-slot:selection="{ attrs, item, parent, selected }">
                        <v-chip
                            v-bind="attrs"
                            :input-value="selected"
                            small
                            close
                            @click:close="parent.selectItem(item)"
                        >
                            {{ item }}
                        </v-chip>
                    </template>
                </v-combobox>

                <!-- Signature -->
                <v-select
                    v-model="signatureSelected"
                    :items="signatures"
                    label="Firma"
                    hint="Seleziona una firma da inserire nella mail"
                    persistent-hint
                    return-object
                    outlined
                    dense
                    class="mb-3"
                    clearable
                    :menu-props="{'offset-y': true}"
                    :loading="loadingSignatures"
                >
                    <template v-slot:item="data">
                        <v-list-item-avatar size="32px" class="primary mr-4">
                            <v-icon color="white" small>mdi-draw</v-icon>
                        </v-list-item-avatar>
                        <v-list-item-content>
                            <v-list-item-title>{{ data.item.text }}</v-list-item-title>
                            <v-list-item-subtitle>{{ data.item.subtitle }}</v-list-item-subtitle>
                        </v-list-item-content>
                    </template>
                </v-select>

                <!-- Optional Note -->
                <div class="mb-3">
                    <label class="mb-2 d-block">Nota (opzionale, verr&agrave; aggiunta prima della conversazione inoltrata)</label>
                    <tinymce-editor
                        :editor-id="'forward-mail-text'"
                        v-model="form.body"
                    />
                </div>

                <!-- Attachments (toggle) -->
                <v-expand-transition>
                    <file-uploader
                        v-if="showAttachments && fileUploaderInfo.attachableType"
                        class="pa-0"
                        :id="ticketId"
                        :attachable-type="fileUploaderInfo.attachableType"
                        :options="fileUploaderInfo.options || {}"
                        :field="fileUploaderInfo"
                        :start-empty="true"
                    ></file-uploader>
                </v-expand-transition>
            </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-3">
            <v-btn
                text
                @click="showAttachments = !showAttachments"
                :color="showAttachments ? 'primary' : 'grey'"
            >
                <v-icon left>mdi-paperclip</v-icon>
                Allegati
                <v-icon right small>{{ showAttachments ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
                text
                @click="$emit('cancel')"
                :disabled="sending"
            >
                Annulla
            </v-btn>
            <v-btn
                color="purple"
                @click="validateFields"
                :loading="sending"
                :disabled="!form.to"
                dark
            >
                <v-icon left>mdi-share</v-icon>
                Inoltra
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import FileUploader from '@/components/common/FileUploader.vue'
import InputMixin from '@/mixins/InputMixin'

export default {
    name: 'EmailForwardForm',

    mixins: [InputMixin],

    components: {
        FileUploader
    },

    props: {
        ticketId: {
            type: Number,
            required: true
        }
    },

    data() {
        return {
            valid: false,
            sending: false,
            form: {
                to: '',
                body: '',
                cc: []
            },
            emailsTo: [],
            signatures: [],
            signatureSelected: null,
            loadingSignatures: false,
            fileUploaderInfo: {},
            attachmentIds: [],
            showAttachments: false
        }
    },

    watch: {
        signatureSelected(newVal, oldVal) {
            let currentBody = this.form.body || ''

            // Rimuovi vecchia firma se presente
            if (oldVal && oldVal.body) {
                const separatorPatterns = [
                    '<p><br></p><p>--</p>' + oldVal.body,
                    '<p>--</p>' + oldVal.body,
                    '<p><br></p>' + oldVal.body,
                    oldVal.body
                ]

                for (const pattern of separatorPatterns) {
                    if (currentBody.endsWith(pattern)) {
                        currentBody = currentBody.substring(0, currentBody.length - pattern.length)
                        break
                    }
                }
            }

            // Aggiungi nuova firma se selezionata, sempre preceduta dal separatore "--"
            // (anche se il body è vuoto: deve sempre apparire "-- / firma" come nei
            // classici client di posta).
            if (newVal && newVal.body) {
                const startsWithSeparator = newVal.body.startsWith('<p>--</p>')
                const hasContent = currentBody.trim().length > 0
                let prefix = ''
                if (startsWithSeparator) {
                    prefix = hasContent ? '<p><br></p>' : ''
                } else {
                    prefix = hasContent ? '<p><br></p><p>--</p>' : '<p>--</p>'
                }
                currentBody += prefix + newVal.body
            }

            this.form.body = currentBody
        }
    },

    methods: {
        async validateFields() {
            const result = await this.$validator.validateAll()
            if (result) {
                this.sendForward()
            }
        },

        async sendForward() {
            this.sending = true

            try {
                await this.axios.post(`helpdesktroubles/${this.ticketId}/forward`, {
                    to: this.form.to,
                    body: this.form.body,
                    cc: this.form.cc,
                    attachment_ids: this.attachmentIds
                })

                this.$emit('forwarded')
                this.clearForm()

            } catch (error) {
                console.error('Error forwarding email:', error)
                this.$store.commit('setSnackbar', {
                    value: true,
                    text: error.response?.data?.message || 'Errore durante l\'inoltro',
                    color: 'error',
                    timeout: 5000
                })
            } finally {
                this.sending = false
            }
        },

        clearForm() {
            this.form = {
                to: '',
                body: '',
                cc: []
            }
            this.attachmentIds = []
            this.showAttachments = false
            this.$validator.reset()
        },

        async loadInfo() {
            this.loadingSignatures = true
            try {
                const response = await this.axios.get(`helpdesktroubles/${this.ticketId}/send-mail-info`)
                this.signatures = response.data.data.signatures || []
                this.fileUploaderInfo = response.data.data.file_uploader_info || {}
                this.emailsTo = response.data.data.emails_to || []

                // Seleziona firma default se presente
                if (response.data.data.signature_default_id) {
                    this.signatureSelected = this.signatures.find(
                        s => s.value === response.data.data.signature_default_id
                    )
                }
            } catch (error) {
                console.error('Error loading info:', error)
            } finally {
                this.loadingSignatures = false
            }
        }
    },

    mounted() {
        this.$eventBusUtility.$on('paths', (files) => {
            this.attachmentIds = files
        })
    },

    beforeDestroy() {
        this.$eventBusUtility.$off('paths')
    },

    created() {
        this.loadInfo()
    }
}
</script>
